import * as WebFont from "webfontloader"
import uniq from "lodash-es/uniq"
import groupBy from "lodash-es/groupBy"

const root = document.documentElement

export function setupStyleSheet(styleSheet: AppStyleSheet) {
  const s = styleSheet

  loadFonts(styleSheet)

  function max(max: number, size: number): number {
    if (size > max) return max
    return size
  }
  function pxToRem(size: number): string {
    return `${size / s.regularTextFontSize}rem`
  }

  //Background
  setVar("--aiden-app-background-color", s.backgroundColor)
  setVar(
    "--aiden-start-page-background-color",
    s.startPage.startPageBackgroundColor
  )
  setVar(
    "--aiden-advice-page-bottom-background-color",
    s.advicePage.bottomBackgroundColor
  )
  setVar(
    "--aiden-advice-page-header-background-color",
    s.advicePage.topBackgroundColor
  )

  //  Answer options
  setVar(
    "--aiden-answer-option-background-color",
    s.answerOptionBackgroundColor
  )
  setVar("--aiden-answer-option-border-color", s.answerOptionBorderColor)
  setVar(
    "--aiden-answer-option-border-color-selected",
    s.answerOptionBorderColorSelected
  )
  setVar(
    "--aiden-answer-option-border-radius",
    toPx(s.answerOptionBorderRadius)
  )
  setVar("--aiden-answer-option-border-width", toPx(s.answerOptionBorderWidth))

  setVar("--aiden-highlight-color", s.formInputHighlightColor)
  setVar("--aiden-checkbox-border-color", s.formInputBorderColor)
  setVar("--aiden-checkbox-background-color", s.formInputBackgroundColor)
  setVar(
    "--aiden-checkbox-border-color-checked",
    s.answerOptionCheckboxBorderColorSelected
  )
  setVar(
    "--aiden-checkbox-background-color-checked",
    s.answerOptionCheckboxColorSelected
  )
  setVar(
    "--aiden-checkbox-check-color-checked",
    s.answerOptionCheckboxCheckColorSelected
  )

  setVar(
    "--aiden-answer-option-checkbox-background-color",
    s.answerOptionCheckboxColor
  )
  setVar(
    "--aiden-answer-option-checkbox-background-color-selected",
    s.answerOptionCheckboxColorSelected
  )
  setVar(
    "--aiden-answer-option-checkbox-color-selected",
    s.answerOptionCheckboxCheckColorSelected
  )
  setVar(
    "--aiden-answer-option-checkbox-border-color",
    s.answerOptionCheckboxBorderColor
  )
  setVar(
    "--aiden-answer-option-checkbox-border-color-selected",
    s.answerOptionCheckboxBorderColorSelected
  )
  setVar("--aiden-answer-option-chevron-color", s.answerOptionChevronColor)
  setVar(
    "--aiden-answer-option-chevron-color-selected",
    s.answerOptionChevronColorSelected
  )
  setVar(
    "--aiden-answer-option-text-font-size",
    toPx(s.answerOptionTextFontSize)
  )
  setVar("--aiden-answer-option-text-color", s.answerOptionTextColor)
  setVar(
    "--aiden-answer-option-font-family",
    s.answerOptionFont // >> fontToCssValue
  )
  setVar(
    "--aiden-answer-option-text-font-family",
    toFontFamily(s.regularTextFont) // >> fontToCssValue
  )

  setVar("--aiden-input-height", "3.5rem")
  setVar("--aiden-checkbox-border-radius", toPx(s.checkboxBorderRadius))
  setVar("--aiden-plane-color", s.formBackgroundColor)
  setVar("--aiden-plane-border-radius", toPx(s.formBorderRadius))
  setVar("--aiden-input-border-width", toPx(s.formInputBorderWidth))
  setVar("--aiden-input-border-color", s.formInputBorderColor)
  setVar("--aiden-input-border-color-focus", s.formInputHighlightColor)
  setVar("--aiden-input-border-radius", toPx(s.formInputBorderRadius))
  setVar("--aiden-input-background-color", s.formInputBackgroundColor)
  setVar("--aiden-input-hint-color", s.formInputPlaceholderColor)
  setVar("--aiden-input-label-font-family", toFontFamily(s.regularTextFont))
  setVar(
    "--aiden-input-label-font-weight",
    fontWeightToCssValue(s.formLabelFontWeight)
  )
  setVar("--aiden-input-label-color", s.formLabelTextColor)
  setVar("--aiden-input-font-color", toFontFamily(s.formInputTextColor))
  setVar("--aiden-input-font-family", toFontFamily(s.regularTextFont))
  setVar(
    "--aiden-input-font-weight",
    fontWeightToCssValue(s.formInputFontWeight)
  )
  setVar("--aiden-progress-bar-track-color", s.progressBarColor)
  setVar("--aiden-progress-bar-color", s.progressBarProgressColor)
  setVar("--aiden-progress-bar-border-radius", toPx(s.progressBarBorderRadius))

  setVar("--aiden-title-color", s.titleColor)
  setVar("--aiden-title-font-weight", fontWeightToCssValue(s.titleFontWeight))

  setVar("--aiden-title-font", toFontFamily(s.titleFont))
  setVar("--aiden-title-font-size", pxToRem(s.titleFontSize))

  setVar("--aiden-link-color", s.linkColor)
  setVar("--aiden-advice-page-header-link-color", s.advicePage.headerLinkColor)
  setVar("--aiden-start-page-link-color", s.startPage.startPageLinkColor)

  setVar("--aiden-regular-text-color", s.regularTextColor)
  setVar(
    "--aiden-regular-text-font-weight",
    fontWeightToCssValue(s.regularTextFontWeight)
  )
  setVar(
    "--aiden-regular-text-font",
    toFontFamily(s.regularTextFont) // >> fontToCssValue
  )
  setVar("--aiden-regular-text-font-size", pxToRem(s.regularTextFontSize))
  setVar("--aiden-font-size", `${s.regularTextFontSize}px`)
  setVar("--aiden-small-text-color", s.regularTextColor)
  setVar(
    "--aiden-small-text-font-weight",
    fontWeightToCssValue(s.regularTextFontWeight)
  )

  setVar("--aiden-small-text-font", toFontFamily(s.regularTextFont))
  setVar("--aiden-small-text-font-size", pxToRem(s.regularTextFontSize))

  // Buttons
  setVar(
    "--aiden-primary-button-background-color",
    s.primaryButton.backgroundColor
  )
  setVar("--aiden-primary-button-border-color", s.primaryButton.borderColor)
  setVar(
    "--aiden-primary-button-border-width",
    toPx(s.primaryButton.borderWidth)
  )
  setVar(
    "--aiden-primary-button-vertical-padding",
    pxToRem(max(16, s.primaryButton.verticalPadding))
  )
  setVar(
    "--aiden-primary-button-horizontal-padding",
    pxToRem(s.primaryButton.horizontalPadding)
  )
  setVar("--aiden-primary-button-color", s.primaryButton.color)
  setVar(
    "--aiden-primary-button-font-weight",
    fontWeightToCssValue(s.primaryButton.fontWeight)
  )

  setVar("--aiden-primary-button-font", toFontFamily(s.primaryButton.font))
  setVar("--aiden-primary-button-font-size", pxToRem(s.primaryButton.fontSize))
  setVar(
    "--aiden-primary-button-text-case",
    toTextTransform(s.primaryButton.textCase)
  )
  setVar(
    "--aiden-primary-button-border-radius",
    toPx(s.primaryButton.borderRadius)
  )
  setVar(
    "--aiden-primary-button-background-color-hover",
    s.primaryButtonBackgroundColorHover
  )
  setVar("--aiden-primary-button-color-hover", s.primaryButtonColorHover)
  setVar(
    "--aiden-primary-button-border-color-hover",
    s.primaryButtonBorderColorHover
  )

  setVar(
    "--aiden-secondary-button-background-color",
    s.secondaryButtonHasBackground
      ? s.secondaryButton.backgroundColor
      : "transparent"
  )
  setVar("--aiden-secondary-button-border-color", s.secondaryButton.borderColor)
  setVar(
    "--aiden-secondary-button-border-width",
    toPx(s.secondaryButton.borderWidth)
  )
  setVar(
    "--aiden-secondary-button-vertical-padding",
    pxToRem(max(16, s.secondaryButton.verticalPadding))
  )
  setVar(
    "--aiden-secondary-button-horizontal-padding",
    pxToRem(s.secondaryButton.horizontalPadding)
  )
  setVar("--aiden-secondary-button-color", s.secondaryButton.color)
  setVar(
    "--aiden-secondary-button-font-weight",
    fontWeightToCssValue(s.secondaryButton.fontWeight)
  )
  setVar(
    "--aiden-secondary-button-font",
    toFontFamily(s.secondaryButton.font) // >> fontToCssValue
  )
  setVar(
    "--aiden-secondary-button-font-size",
    pxToRem(s.secondaryButton.fontSize)
  )
  setVar(
    "--aiden-secondary-button-text-case",
    toTextTransform(s.secondaryButton.textCase)
  )
  setVar(
    "--aiden-secondary-button-border-radius",
    toPx(s.secondaryButton.borderRadius)
  )
  setVar(
    "--aiden-secondary-button-background-color-hover",
    s.secondaryButtonBackgroundColorHover
  )
  setVar("--aiden-secondary-button-color-hover", s.secondaryButtonColorHover)
  setVar(
    "--aiden-secondary-button-border-color-hover",
    s.secondaryButtonBorderColorHover
  )

  // Navigation buttons
  setVar("--aiden-navigation-button-color", s.navigationButtonColor)
  setVar(
    "--aiden-navigation-button-font-weight",
    fontWeightToCssValue(s.navigationButtonFontWeight)
  )
  setVar(
    "--aiden-navigation-button-text-case",
    toTextTransform(s.navigationButtonTextCase)
  )
  setVar("--aiden-navigation-button-font", toFontFamily(s.navigationButtonFont))
  setVar(
    "--aiden-navigation-button-border-radius",
    pxToRem(s.navigationButtonBorderRadius)
  )
  setVar(
    "--aiden-navigation-button-background-color-hover",
    s.navigationButtonBackgroundColorHover
  )
  setVar("--aiden-navigation-button-color-hover", s.navigationButtonColorHover)
  setVar(
    "--aiden-primary-button-background-color",
    s.primaryButton.backgroundColor
  )
  setVar("--aiden-primary-button-border-color", s.primaryButton.borderColor)
  setVar(
    "--aiden-primary-button-border-width",
    toPx(s.primaryButton.borderWidth)
  )
  setVar(
    "--aiden-primary-button-vertical-padding",
    toPx(s.primaryButton.verticalPadding)
  )
  setVar(
    "--aiden-primary-button-horizontal-padding",
    toPx(s.primaryButton.horizontalPadding)
  )
  setVar("--aiden-primary-button-color", s.primaryButton.color)
  setVar(
    "--aiden-primary-button-font-weight",
    fontWeightToCssValue(s.primaryButton.fontWeight)
  )

  setVar("--aiden-primary-button-font", toFontFamily(s.primaryButton.font))
  setVar("--aiden-primary-button-font-size", pxToRem(s.primaryButton.fontSize))
  setVar(
    "--aiden-primary-button-text-case",
    toTextTransform(s.primaryButton.textCase)
  )
  setVar(
    "--aiden-primary-button-border-radius",
    toPx(s.primaryButton.borderRadius)
  )
  setVar(
    "--aiden-primary-button-background-color-hover",
    s.primaryButtonBackgroundColorHover
  )
  setVar("--aiden-primary-button-color-hover", s.primaryButtonColorHover)
  setVar(
    "--aiden-primary-button-border-color-hover",
    s.primaryButtonBorderColorHover
  )
  // Start page
  setVar(
    "--aiden-start-page-text-align",
    s.startPage.center ? "center" : "left"
  )
  setVar(
    "--aiden-start-page-justify-content",
    s.startPage.center ? "center" : "start"
  )

  setVar(
    "--aiden-start-page-title-font-size",
    pxToRem(s.startPage.startPageTitleFontSize)
  )

  setVar("--aiden-start-page-title-color", s.startPage.startPageTitleFontColor)
  setVar(
    "--aiden-start-page-regular-text-font-size",
    pxToRem(s.startPage.startPageRegularTextFontSize)
  )

  setVar(
    "--aiden-start-page-regular-text-color",
    s.startPage.startPageRegularTextFontColor
  )
  setVar(
    "--aiden-start-page-small-text-color",
    s.startPage.startPageRegularTextFontColor
  )
  // setVar(
  //   "--aiden-start-page-small-text-font-size",
  //   pxToRem(s.startPage.startPageSmallTextFontSize)
  // )

  setVar(
    "--aiden-start-page-navigation-button-background-color-hover",
    s.startPage.navigationButtonBackgroundColorHover
  )
  setVar(
    "--aiden-start-page-navigation-button-color-hover",
    s.startPage.navigationButtonColorHover
  )
  setVar(
    "--aiden-start-page-navigation-button-color",
    s.startPage.navigationButtonColor
  )

  // Advice page

  setVar(
    "--aiden-advice-page-header-title-font-size",
    pxToRem(s.advicePage.headerTitleFontSize)
  )
  setVar("--aiden-advice-page-header-title-font", toFontFamily(s.titleFont))

  setVar(
    "--aiden-advice-page-header-title-font-weight",
    fontWeightToCssValue(s.titleFontWeight)
  )
  setVar(
    "--aiden-advice-page-header-title-color",
    s.advicePage.headerTitleFontColor
  )
  setVar(
    "--aiden-advice-page-header-regular-text-font-size",
    pxToRem(s.advicePage.headerRegularTextFontSize)
  )
  setVar(
    "--aiden-advice-page-header-regular-text-font",
    toFontFamily(s.regularTextFont)
  )
  setVar(
    "--aiden-advice-page-header-regular-text-font-weight",
    fontWeightToCssValue(s.regularTextFontWeight)
  )
  setVar(
    "--aiden-advice-page-header-regular-text-color",
    s.advicePage.headerRegularTextFontColor
  )
  setVar(
    "--aiden-advice-page-header-small-text-color",
    s.advicePage.headerRegularTextFontColor
  )
  setVar(
    "--aiden-advice-page-header-small-text-font-size",
    pxToRem(s.advicePage.headerRegularTextFontSize)
  )
  setVar(
    "--aiden-advice-page-header-small-text-font",
    toFontFamily(s.regularTextFont)
  )
  // >> fontToCssValue

  // )
  setVar(
    "--aiden-advice-page-header-small-text-font-weight",
    fontWeightToCssValue(s.regularTextFontWeight)
  )
  setVar(
    "--aiden-advice-page-header-small-text-color",
    s.advicePage.headerRegularTextFontColor
  )

  setVar(
    "--aiden-advice-page-bottom-navigation-button-background-color-hover",
    s.advicePage.bottomNavigationButtonBackgroundColorHover
  )
  setVar(
    "--aiden-advice-page-bottom-navigation-button-color-hover",
    s.advicePage.bottomNavigationButtonColorHover
  )
  setVar(
    "--aiden-advice-page-bottom-navigation-button-color",
    s.advicePage.bottomNavigationButtonColor
  )
  setVar(
    "--aiden-advice-page-top-navigation-button-background-color-hover",
    s.advicePage.topNavigationButtonBackgroundColorHover
  )
  setVar(
    "--aiden-advice-page-top-navigation-button-color-hover",
    s.advicePage.topNavigationButtonColorHover
  )
  setVar(
    "--aiden-advice-page-top-navigation-button-color",
    s.advicePage.topNavigationButtonColor
  )

  // Product card
  const productCard = s.advicePage.productCard
  setVar(
    "--aiden-bad-match-label-background-color",
    productCard.neutralMatchLabelBackgroundColor
  )
  setVar(
    "--aiden-bad-match-label-border",
    toPx(productCard.matchLabelBorderWidth)
  )
  setVar(
    "--aiden-bad-match-label-border-radius",
    toPx(productCard.matchLabelBorderRadius)
  )
  setVar(
    "--aiden-bad-match-label-font-weight",
    fontWeightToCssValue(productCard.matchLabelFontWeight)
  )
  setVar(
    "--aiden-bad-match-label-text-color",
    productCard.neutralMatchLabelTextColor
  )
  setVar(
    "--aiden-best-match-label-background-color",
    productCard.bestMatchLabelBackgroundColor
  )
  setVar(
    "--aiden-best-match-label-border",
    toPx(productCard.matchLabelBorderWidth)
  )
  setVar(
    "--aiden-best-match-label-border-radius",
    toPx(productCard.matchLabelBorderRadius)
  )
  setVar(
    "--aiden-best-match-label-font-weight",
    fontWeightToCssValue(productCard.matchLabelFontWeight)
  )
  setVar(
    "--aiden-best-match-label-text-color",
    productCard.bestMatchLabelTextColor
  )
  setVar("--aiden-match-label-font-family", productCard.matchLabelFontFamily)
  setVar("--aiden-price-color", productCard.priceColor)
  setVar("--aiden-product-price-font-family", productCard.priceFontFamily)
  setVar("--aiden-price-font-size", pxToRem(productCard.priceFontSize))
  setVar(
    "--aiden-price-font-weight",
    fontWeightToCssValue(productCard.priceFontWeight)
  )
  setVar("--aiden-price-prefix-color", productCard.productCardTextColor)
  setVar(
    "--aiden-price-prefix-font-size",
    pxToRem(productCard.pricePrefixFontSize)
  )
  setVar(
    "--aiden-price-prefix-font-weight",
    fontWeightToCssValue(productCard.pricePrefixFontWeight)
  )
  setVar("--aiden-price-suffix-color", productCard.productCardTextColor)
  setVar(
    "--aiden-price-suffix-font-size",
    pxToRem(productCard.priceSuffixFontSize)
  )
  setVar(
    "--aiden-price-suffix-font-weight",
    fontWeightToCssValue(productCard.priceSuffixFontWeight)
  )
  setVar(
    "--aiden-product-card-background-color",
    productCard.productCardBackgroundColor
  )
  setVar(
    "--aiden-product-card-border-color",
    productCard.productCardBorderColor
  )
  setVar(
    "--aiden-product-card-border-radius",
    toPx(productCard.productCardBorderRadius)
  )
  setVar("--aiden-product-card-text-color", productCard.productCardTextColor)
  setVar(
    "--aiden-product-field-font-size",
    pxToRem(productCard.productFieldFontSize)
  )
  setVar("--aiden-product-field-label-color", productCard.productCardTextColor)
  setVar(
    "--aiden-product-field-label-font-weight",
    fontWeightToCssValue(productCard.productFieldLabelFontWeight)
  )
  setVar("--aiden-product-field-suffix-color", productCard.productCardTextColor)
  setVar(
    "--aiden-product-field-suffix-font-weight",
    fontWeightToCssValue(productCard.productFieldSuffixFontWeight)
  )
  setVar("--aiden-product-field-value-color", productCard.productCardTextColor)
  setVar(
    "--aiden-product-field-value-font-weight",
    fontWeightToCssValue(productCard.productFieldValueFontWeight)
  )
  setVar("--aiden-product-name-color", productCard.productNameColor)
  setVar("--aiden-product-name-font-family", productCard.productNameFontFamily)
  setVar(
    "--aiden-product-name-font-size",
    pxToRem(productCard.productNameFontSize)
  )
  setVar(
    "--aiden-product-name-font-weight",
    fontWeightToCssValue(productCard.productNameFontWeight)
  )
  setVar("--aiden-sale-price-color", productCard.salePriceColor)
  setVar("--aiden-sale-price-font-size", pxToRem(productCard.salePriceFontSize))
  setVar(
    "--aiden-sale-price-font-weight",
    fontWeightToCssValue(productCard.salePriceFontWeight)
  )
  setVar(
    "--aiden-sale-text-decoration",
    productCard.saleTextStrikeThrough ? "line-through" : "none"
  )

  setVar("--aiden-inpage-background", s.backgroundColor)
  setVar(
    "--aiden-inpage-background-start",
    s.startPage.startPageBackgroundColor
  )
  setVar("--aiden-inpage-background-advice", s.advicePage.bottomBackgroundColor)

  // #review score
  setVar("--aiden-review-star-color", productCard.reviewStarColor)
  setVar("--aiden-review-star-fill-color", productCard.reviewStarFillColor)
}

function setVar(name: string, value: string) {
  root.style.setProperty(name, value)
}

export type AppStyleSheet = {
  // Basics
  backgroundColor: ColorValue
  // Basics -- Title
  titleColor: ColorValue
  titleFontWeight: FontWeight
  titleFont: FontFamily
  titleFontSize: FontSizeValue
  titleTextCase: TextCase
  // Basics -- Text
  regularTextColor: ColorValue
  regularTextFontWeight: FontWeight
  regularTextFont: FontFamily
  regularTextFontSize: FontSizeValue
  linkColor: ColorValue
  // Primary button
  primaryButton: ButtonStyle
  // Primary button -- Hover
  primaryButtonBackgroundColorHover: ColorValue
  primaryButtonColorHover: ColorValue
  primaryButtonBorderColorHover: ColorValue
  // Secondary button
  secondaryButtonHasBackground: Boolean
  secondaryButton: ButtonStyle
  secondaryButtonBackgroundColorHover: ColorValue
  secondaryButtonColorHover: ColorValue
  secondaryButtonBorderColorHover: ColorValue
  // Navigation -- Navigation button
  navigationButtonColor: ColorValue
  navigationButtonFontWeight: FontWeight
  navigationButtonFont: FontFamily
  navigationButtonFontSize: FontSizeValue
  navigationButtonBorderRadius: PixelValue
  navigationButtonTextCase: TextCase
  // Navigation button -- Hover
  navigationButtonBackgroundColorHover: ColorValue
  navigationButtonColorHover: ColorValue
  navigationButtonBorderColorHover: ColorValue
  // Navigation -- Progress bar
  progressBarColor: ColorValue
  progressBarBorderRadius: PixelValue
  progressBarProgressColor: ColorValue
  // Answer Options
  answerOptionBorderRadius: PixelValue
  answerOptionBorderWidth: PixelValue
  answerOptionTextFontSize: PixelValue
  answerOptionFont: FontFamily
  answerOptionTextFont: FontFamily
  answerOptionBackgroundColor: ColorValue
  answerOptionBorderColor: ColorValue
  answerOptionBorderColorSelected: ColorValue
  answerOptionCheckboxColor: ColorValue
  answerOptionCheckboxColorSelected: ColorValue
  answerOptionCheckboxCheckColorSelected: ColorValue
  answerOptionCheckboxBorderColor: ColorValue
  answerOptionCheckboxBorderColorSelected: ColorValue
  answerOptionChevronColor: ColorValue
  answerOptionChevronColorSelected: ColorValue
  answerOptionTextColor: ColorValue
  // Checkbox
  checkboxBorderRadius: PixelValue
  formInputHighlightColor: ColorValue
  // Form elements
  formFontFamily: FontFamily
  formBackgroundColor: ColorValue
  formBorderRadius: PixelValue
  formInputBackgroundColor: ColorValue
  formInputBorderColor: ColorValue
  formInputBorderWidth: PixelValue
  formInputBorderRadius: PixelValue
  formInputBorderColorFocus: ColorValue
  formInputTextColor: ColorValue
  formInputFontWeight: FontWeight
  formInputPlaceholderColor: ColorValue
  formLabelTextColor: ColorValue
  formLabelFontWeight: FontWeight
  // Start Page
  startPage: StartPageStyleSheet
  // Advice Page
  advicePage: AdvicePageStyleSheet
}

type ButtonStyle = {
  backgroundColor: ColorValue
  borderColor: ColorValue
  borderWidth: PixelValue
  verticalPadding: PixelValue
  horizontalPadding: PixelValue
  color: ColorValue
  fontWeight: FontWeight
  font: FontFamily
  fontSize: FontSizeValue
  borderRadius: PixelValue
  textCase: TextCase
}

type StartPageStyleSheet = {
  startPageBackgroundColor: ColorValue
  startPageTitleFontSize: FontSizeValue
  startPageTitleFontColor: ColorValue
  startPageRegularTextFontSize: FontSizeValue
  startPageRegularTextFontColor: ColorValue
  // startPageSmallTextFontSize: FontSizeValue
  // startPageSmallTextFontColor: ColorValue
  startPageLinkColor: ColorValue

  navigationButtonBackgroundColorHover: ColorValue
  navigationButtonColorHover: ColorValue
  navigationButtonColor: ColorValue

  center: Boolean
}

type AdvicePageStyleSheet = {
  topBackgroundColor: ColorValue
  headerTitleFontSize: FontSizeValue
  headerTitleFontColor: ColorValue
  headerRegularTextFontSize: FontSizeValue
  headerRegularTextFontColor: ColorValue
  // headerSmallTextFontSize: FontSizeValue
  // headerSmallTextFontColor: ColorValue
  headerLinkColor: ColorValue
  bottomBackgroundColor: ColorValue

  bottomNavigationButtonBackgroundColorHover: ColorValue
  bottomNavigationButtonColorHover: ColorValue
  bottomNavigationButtonColor: ColorValue

  topNavigationButtonBackgroundColorHover: ColorValue
  topNavigationButtonColorHover: ColorValue
  topNavigationButtonColor: ColorValue

  productCard: ProductCardStyleSheet
}

type ProductCardStyleSheet = {
  productCardBackgroundColor: ColorValue
  productCardBorderRadius: PixelValue
  productCardBorderColor: ColorValue
  productCardTextColor: ColorValue
  productFieldFontSize: FontSizeValue
  productFieldLabelFontWeight: FontWeight
  // productFieldLabelColor: ColorValue,
  productFieldValueFontWeight: FontWeight
  // productFieldValueColor: ColorValue,
  productFieldSuffixFontWeight: FontWeight
  // productFieldSuffixColor: ColorValue,
  productNameColor: ColorValue
  productNameFontWeight: FontWeight
  productNameFontSize: FontSizeValue
  productNameFontFamily: FontFamily
  priceColor: ColorValue
  priceFontWeight: FontWeight
  priceFontSize: FontSizeValue
  priceFontFamily: FontFamily
  pricePrefixColor: ColorValue
  pricePrefixFontWeight: FontWeight
  pricePrefixFontSize: FontSizeValue
  priceSuffixColor: ColorValue
  priceSuffixFontWeight: FontWeight
  priceSuffixFontSize: FontSizeValue
  salePriceFontSize: FontSizeValue
  salePriceFontWeight: FontWeight
  saleTextStrikeThrough: Boolean
  salePriceColor: ColorValue
  matchLabelBorderRadius: PixelValue
  matchLabelFontWeight: FontWeight
  matchLabelFontFamily: FontFamily
  matchLabelBorderWidth: PixelValue
  bestMatchLabelBackgroundColor: ColorValue
  bestMatchLabelTextColor: ColorValue
  neutralMatchLabelBackgroundColor: ColorValue
  neutralMatchLabelTextColor: ColorValue
  reviewStarFillColor: ColorValue
  reviewStarColor: ColorValue
}

type ColorValue = string

type FontFamily = string

type FontSizeValue = number

type PixelValue = number

type FontWeight = "Light" | "Regular" | "SemiBold" | "Bold" | "ExtraBold"

type TextCase = "NormalCase" | "UpperCase"

function toPx(v: any) {
  return `${v}px`
}

function toTextTransform(transform: TextCase): string {
  if (transform === "UpperCase") return "uppercase"
  return "normal"
}

function toFontFamily(family: string): string {
  return `"${family}", system-ui, sans-serif`
}

function fontWeightToCssValue(fontWeight: FontWeight | string): string {
  if (fontWeight === "Regular") return "400"

  if (fontWeight === "Light") return "300"

  if (fontWeight === "SemiBold") return "500"

  if (fontWeight === "Bold") return "600"

  if (fontWeight === "ExtraBold") return "800"
  return "400"
}
function loadFonts(styleSheet: AppStyleSheet) {
  const fontSpecs = [
    { font: styleSheet.titleFont, weight: styleSheet.titleFontWeight },
    {
      font: styleSheet.regularTextFont,
      weight: styleSheet.regularTextFontWeight,
    },
    {
      font: styleSheet.regularTextFont,
      weight: "Bold",
    },
    {
      font: styleSheet.primaryButton.font,
      weight: styleSheet.primaryButton.fontWeight,
    },
    {
      font: styleSheet.secondaryButton.font,
      weight: styleSheet.secondaryButton.fontWeight,
    },
    {
      font: styleSheet.advicePage.productCard.matchLabelFontFamily,
      weight: styleSheet.advicePage.productCard.matchLabelFontWeight,
    },
    {
      font: styleSheet.advicePage.productCard.priceFontFamily,
      weight: styleSheet.advicePage.productCard.priceFontWeight,
    },
    {
      font: styleSheet.advicePage.productCard.productNameFontFamily,
      weight: styleSheet.advicePage.productCard.productNameFontWeight,
    },
  ]

  const grouped: Record<string, { font: string; weight: string }[]> = groupBy(
    fontSpecs,
    (s) => s.font
  )

  const families: string[] =
    Object.entries(grouped).map(([fam, obj]) => {
      const weights = uniq(obj.map((o) => fontWeightToCssValue(o.weight)))
        .sort()
        .join(",")

      return `${fam}:${weights}`
    })

  const urls = families
    .map((f) => f.replace(" ", "+"))
    .map((p) => `https://fonts.bunny.net/css?family=${p}&display=swap`)

  WebFont.load({
    custom: {
      families: families,
      urls: urls,
    },
  })
}
